import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VideoTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: "Nisha Kumari",
      location: "Mumbai, Maharashtra",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      videoUrl: "/videos/testimonial1.mp4",
      videoThumbnail: "/videos/thumbnails/testimonial1.jpg",
      winAmount: "₹2,50,000",
      game: "Teen Patti Gold",
      quote: `I've been playing on Nitin for 8 months now. The WhatsApp support is incredible - they helped me understand strategies and I won ₹2.5 lakhs last month! The payouts are instant, exactly as promised.`,
      rating: 5,
      verified: true,
      playTime: "2:34"
    },
    {
      id: 2,
      name: "Priya Sharma",
      location: "Delhi, NCR",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      videoUrl: "/videos/testimonial2.mp4",
      videoThumbnail: "/videos/thumbnails/testimonial2.jpg",
      winAmount: "₹1,75,000",
      game: "Andar Bahar Royal",
      quote: `As a working professional, I needed a platform I could trust. Nitin's personal WhatsApp guidance helped me win consistently. The security and fair play are unmatched in India.`,
      rating: 5,
      verified: true,
      playTime: "3:12"
    },
    {
      id: 3,
      name: "Amit Patel",
      location: "Ahmedabad, Gujarat",
      avatar: "https://randomuser.me/api/portraits/men/56.jpg",
      videoUrl: "/videos/testimonial3.mp4",
      videoThumbnail: "/videos/thumbnails/testimonial3.jpg",
      winAmount: "₹4,20,000",
      game: "European Roulette",
      quote: `Started with small bets, but with Nitin's expert guidance through WhatsApp, I learned advanced strategies. Won ₹4.2 lakhs in 3 months! The platform is genuinely trustworthy.`,
      rating: 5,
      verified: true,
      playTime: "2:56"
    },
    {
      id: 4,
      name: "Sneha Reddy",
      location: "Hyderabad, Telangana",
      avatar: "https://randomuser.me/api/portraits/women/68.jpg",
      videoUrl: "/videos/testimonial4.mp4",
      videoThumbnail: "/videos/thumbnails/testimonial4.jpg",
      winAmount: "₹3,10,000",
      game: "Dragon Tiger",
      quote: `The personalized WhatsApp support makes all the difference. They taught me bankroll management and I've been winning consistently. Nitin is the only platform I trust with my money.`,
      rating: 5,
      verified: true,
      playTime: "2:18"
    }
  ];

  const handlePlayVideo = (index) => {
    setActiveTestimonial(index);
    setIsPlaying(true);
  };

  const handleWhatsAppContact = () => {
    const message = "Hello! I saw the testimonials and I'm interested in learning more about winning strategies and getting started with Nitin Online Hub.";
    window.open(`https://wa.me/917704070169?text=${encodeURIComponent(message)}`, '_blank');
  };

  useEffect(() => {
    // Auto-rotate testimonials every 8 seconds
    const interval = setInterval(() => {
      if (!isPlaying) {
        setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [isPlaying, testimonials?.length]);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Play" size={24} color="var(--color-primary)" />
            <span className="text-primary font-medium">SUCCESS STORIES</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-4">
            Real Winners, Real Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Watch authentic testimonials from our winning community. See how personalized WhatsApp guidance helped them achieve consistent success.
          </p>
        </div>

        {/* Main Video Player */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-royal overflow-hidden">
            <div className="relative">
              {/* Video or Thumbnail */}
              <div className="relative h-64 md:h-96 bg-gray-900 overflow-hidden">
                {isPlaying ? (
                  <video
                    ref={videoRef}
                    src={testimonials[activeTestimonial]?.videoUrl}
                    className="w-full h-full object-contain bg-black"
                    controls
                    autoPlay
                    onEnded={() => setIsPlaying(false)}
                  />
                ) : (
                  <>
                    <Image
                      src={testimonials[activeTestimonial]?.videoThumbnail}
                      alt={`${testimonials[activeTestimonial]?.name} testimonial`}
                      className="w-full h-full object-contain bg-black"
                    />
                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <button
                        onClick={() => handlePlayVideo(activeTestimonial)}
                        className="w-20 h-20 bg-primary rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors duration-300 animate-pulse-golden hover:animate-none"
                      >
                        <Icon name="Play" size={32} color="white" />
                      </button>
                    </div>
                  </>
                )}

                {/* Video Duration */}
                <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-white text-sm font-medium">
                    {testimonials[activeTestimonial]?.playTime}
                  </span>
                </div>

                {/* Verified Badge */}
                {testimonials[activeTestimonial]?.verified && (
                  <div className="absolute top-4 right-4 bg-green-500 rounded-full px-3 py-1 flex items-center space-x-1">
                    <Icon name="CheckCircle" size={16} color="white" />
                    <span className="text-white text-sm font-medium">Verified</span>
                  </div>
                )}
              </div>

              {/* Player Info */}
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <Image
                      src={testimonials[activeTestimonial]?.avatar}
                      alt={testimonials[activeTestimonial]?.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    {testimonials[activeTestimonial]?.verified && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {testimonials[activeTestimonial]?.name}
                      </h3>
                      <div className="flex items-center space-x-1">
                        {[...Array(testimonials[activeTestimonial]?.rating)]?.map((_, i) => (
                          <Icon key={i} name="Star" size={16} color="#FFD700" />
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={14} />
                        <span>{testimonials[activeTestimonial]?.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Trophy" size={14} />
                        <span>{testimonials[activeTestimonial]?.game}</span>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={16} color="#22C55E" />
                        <span className="text-green-700 font-semibold">
                          Won {testimonials[activeTestimonial]?.winAmount}
                        </span>
                      </div>
                    </div>

                    <blockquote className="text-gray-700 italic leading-relaxed">
                      "{testimonials[activeTestimonial]?.quote}"
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Thumbnails */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial?.id}
              onClick={() => {
                setActiveTestimonial(index);
                setIsPlaying(false); // reset to thumbnail when switching
              }}
              className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
                index === activeTestimonial 
                  ? 'ring-4 ring-primary shadow-royal' 
                  : 'hover:shadow-subtle'
              }`}
            >
              <div className="aspect-video relative">
                <Image
                  src={testimonial?.videoThumbnail}
                  alt={`${testimonial?.name} testimonial`}
                  className="w-full h-full object-contain bg-black"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                    <Icon name="Play" size={16} color="#000" />
                  </div>
                </div>

                {/* Player Info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <div className="text-white text-xs font-medium truncate">
                    {testimonial?.name}
                  </div>
                  <div className="text-white/80 text-xs">
                    {testimonial?.winAmount}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-royal rounded-2xl p-8 text-white text-center">
          <div className="mb-6">
            <h3 className="text-2xl font-heading font-bold mb-2">
              Ready to Join Our Winners Circle?
            </h3>
            <p className="text-xl opacity-90">
              Get personalized gaming strategies and expert guidance through WhatsApp
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={handleWhatsAppContact}
              iconName="MessageCircle"
              iconPosition="left"
              className="bg-white text-primary hover:bg-gray-100 px-8 py-4"
            >
              Get Winning Strategies
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              iconName="Users"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-primary px-8 py-4"
            >
              View All Success Stories
            </Button>
          </div>

          {/* Social Proof */}
          <div className="mt-8 pt-6 border-t border-white/20">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold">₹12Cr+</div>
                <div className="text-sm opacity-80">Total Winnings Paid</div>
              </div>
              <div>
                <div className="text-2xl font-bold">2,847</div>
                <div className="text-sm opacity-80">Success Stories</div>
              </div>
              <div>
                <div className="text-2xl font-bold">4.9★</div>
                <div className="text-sm opacity-80">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoTestimonials;
